import { isEmpty } from 'ramda'

import { LanguageConfig } from '@/app/module/campaigns/language-helpers'
import { OpenEnded } from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { ActionContainer } from '@/app/module/campaigns/utils/actions/types'

type SenderIdConfig = {
  senderId: string
  senderIdQuestions: string
  senderIdWhatsapp?: string
}

type SenderIdConfigKeys = keyof SenderIdConfig

export function processOpenEndedActions(
  openEnded: OpenEnded | null | undefined,
  languages: LanguageConfig,
  processFn: (actions: ActionContainer<Action[]>, languages: LanguageConfig) => ActionContainer<Action[]>,
): OpenEnded | undefined
export function processOpenEndedActions(
  openEnded: OpenEnded | null | undefined,
  senderIdConfig: SenderIdConfig,
  processFn: (action: ActionContainer<Action[]>, senderIdConfig: SenderIdConfig) => ActionContainer<Action[]>,
): OpenEnded | undefined
export function processOpenEndedActions(
  openEnded: OpenEnded | null | undefined,
  config: LanguageConfig | SenderIdConfig,
  processFn:
    | ((action: ActionContainer<Action[]>, config: SenderIdConfig) => ActionContainer<Action[]>)
    | ((actions: ActionContainer<Action[]>, config: LanguageConfig) => ActionContainer<Action[]>),
): OpenEnded | undefined {
  if (!openEnded) {
    return undefined
  }
  const processed = processActionsInObjects(openEnded, config, processFn)
  if (processed && openEnded.categories?.length) {
    processed.categories = processActionsInLists(processed.categories, config, processFn) || undefined
  }
  return processed || undefined
}

export function processActionsInLists<A extends ActionContainer<Action[]>>(
  answers: A[] | null | undefined,
  senderIds: LanguageConfig | SenderIdConfig,
  processFn: Function,
): A[] | null | undefined {
  if (!answers) {
    return answers
  }
  return answers.map((answer) => processFn(answer, senderIds))
}

export function processActionsInObjects<A extends ActionContainer<Action[]>>(
  answers: A | null | undefined,
  senderIds: LanguageConfig | SenderIdConfig,
  processFn: Function,
): A | null | undefined {
  if (!answers) {
    return answers
  }
  return processFn(answers, senderIds)
}

// Removes empty actions and resolves inherited sender IDs before saving.
export function processActionForSaving<A extends ActionContainer<Action[]>>(
  action: A | null | undefined,
  senderIds: SenderIdConfig,
) {
  const newAnswer = {
    ...action,
    actions: (action?.actions || [])
      .filter((value) => !isEmpty(value))
      .map((value) => {
        if ('sendSMS' in value && value.sendSMS.useCampaignSenderId !== 'action') {
          const { sendSMS } = value
          return {
            ...value,
            sendSMS: {
              ...sendSMS,
              senderId: senderIds[sendSMS.useCampaignSenderId as SenderIdConfigKeys]!,
            },
          }
        }
        if ('sendSMSToPhoneNumbers' in value && value.sendSMSToPhoneNumbers.useCampaignSenderId !== 'action') {
          const { sendSMSToPhoneNumbers } = value
          return {
            ...value,
            sendSMSToPhoneNumbers: {
              ...sendSMSToPhoneNumbers,
              senderId: senderIds[sendSMSToPhoneNumbers.useCampaignSenderId as SenderIdConfigKeys]!,
            },
          }
        }
        if ('sendWhatsapp' in value && value.sendWhatsapp.useCampaignSenderId !== 'action') {
          const { sendWhatsapp } = value
          return {
            ...value,
            sendWhatsapp: {
              ...sendWhatsapp,
              senderId: senderIds[sendWhatsapp.useCampaignSenderId as SenderIdConfigKeys]!,
            },
          }
        }
        return value
      }),
  }
  return newAnswer
}
